import { Mono } from '../../ui/Mono';
import { IsPositionFree, IsPositionOccupied, NormalizeScanCode } from './collectorScan';

/**
 * A TORRE DO BLOCO — o desenho que resolve o problema de não existir etiqueta
 * de andar/posição.
 *
 * A única etiqueta física do rack é a do BLOCO (`A-01`, §4). Depois de
 * bipar o bloco, o operador ainda precisa saber (ou dizer) QUAL das posições
 * daquela coluna é a certa — e é isso que a torre faz: um andar por linha, o
 * mais alto em cima, as duas posições lado a lado, e o alvo ACESO.
 *
 * Correções de leitura do protótipo do dono (registradas como não-conformidade
 * no PRD §9): P1 é a posição da DIREITA de frente para o rack — o protótipo
 * desenhava P1 à esquerda —, e o andar tem UM dígito (`A5`, nunca `A05`).
 *
 * Dois modos:
 *  - LEITURA (`onPick` ausente): mostra o estado do bloco, com o alvo aceso.
 *  - ESCOLHA (`onPick` presente): cada posição é um botão grande; as ocupadas
 *    continuam clicáveis de propósito — escolher uma ocupada é justamente o
 *    caminho do "registrar ocupado" e do 409 da engine, e esconder a opção só
 *    faria o operador achar que a tela travou.
 */

const POSITION_ORDER = [2, 1]; // P2 à esquerda, P1 à direita (de frente para o rack)

const toFinite = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

function cellClasses({ isTarget, isFree, isBlocked, interactive }) {
    if (isTarget) {
        return 'border-success-600 bg-success-soft text-success-text ring-4 ring-success-500/40 animate-pulse dark:border-success-400 dark:bg-success-900/40 dark:text-success-300';
    }
    if (isBlocked) {
        return 'border-mono-300 bg-mono-100 text-mono-400 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-600';
    }
    if (!isFree) {
        return `border-warning-400 bg-warning-soft text-warning-text dark:border-warning-700 dark:bg-warning-900/30 dark:text-warning-300 ${
            interactive ? 'hover:border-warning-600' : ''
        }`;
    }
    return `border-mono-300 bg-white text-black dark:border-mono-700 dark:bg-mono-950 dark:text-white ${
        interactive ? 'hover:border-black dark:hover:border-white' : ''
    }`;
}

/**
 * `pickFilter`: quais células respondem ao toque quando há `onPick` —
 * 'FREE' (colocar pallet: só posição livre e ativa), 'OCCUPIED' (pegar
 * pallet: só posição com pallet) ou 'ANY'. Célula fora do filtro vira
 * mostrador, não botão — o operador não consegue mandar o pallet para cima
 * de outro (o banco barraria, mas com tela vermelha genérica).
 */
const BlockTower = ({ rack, bay, positions = [], targetCode = null, onPick = null, pickFilter = 'ANY', label }) => {
    const normalizedTarget = NormalizeScanCode(targetCode);
    const interactive = typeof onPick === 'function';

    const rows = positions.filter((row) => toFinite(row?.level) !== null);
    const levels = [...new Set(rows.map((row) => toFinite(row.level)))].sort((a, b) => b - a);

    if (levels.length === 0) {
        return (
            <p className="rounded-xl border border-dashed border-mono-300 px-4 py-6 text-center text-sm text-mono-500 dark:border-mono-700 dark:text-mono-400">
                {label('wms.collector.tower_empty')}
            </p>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                    {label('wms.collector.tower_title')}
                </span>
                <Mono className="text-sm font-semibold text-black dark:text-white">
                    {`${String(rack || '').toUpperCase()}-${String(toFinite(bay) ?? 0).padStart(2, '0')}`}
                </Mono>
            </div>

            <div className="space-y-1.5">
                {levels.map((level) => (
                    <div key={level} className="grid grid-cols-[44px_1fr_1fr] gap-1.5">
                        <div className="flex items-center justify-center rounded-lg bg-mono-100 text-sm font-bold text-mono-600 dark:bg-mono-900 dark:text-mono-400">
                            <Mono>{`A${level}`}</Mono>
                        </div>
                        {POSITION_ORDER.map((positionNumber) => {
                            const row = rows.find(
                                (item) =>
                                    toFinite(item.level) === level && toFinite(item.position) === positionNumber,
                            );
                            const key = `${level}-${positionNumber}`;
                            if (!row) {
                                return (
                                    <div
                                        key={key}
                                        className="min-h-[56px] rounded-lg border border-dashed border-mono-200 dark:border-mono-800"
                                    />
                                );
                            }
                            const isTarget = normalizedTarget && NormalizeScanCode(row.code) === normalizedTarget;
                            const isBlocked = String(row.status || 'ACTIVE').toUpperCase() !== 'ACTIVE';
                            const isFree = IsPositionFree(row);
                            const canPick =
                                interactive &&
                                (pickFilter === 'ANY' ||
                                    (pickFilter === 'FREE' ? isFree && !isBlocked : IsPositionOccupied(row)));
                            const classes = `flex min-h-[56px] flex-col items-center justify-center gap-0.5 rounded-lg border-2 px-2 py-2 transition-colors ${cellClasses(
                                { isTarget, isFree, isBlocked, interactive: canPick },
                            )}`;
                            const body = (
                                <>
                                    <span className="text-sm font-bold">{`P${positionNumber}`}</span>
                                    <span className="text-[11px] leading-tight opacity-80">
                                        {isBlocked
                                            ? label(`wms.location_status.${row.status}`)
                                            : label(`wms.occupancy.${isFree ? 'FREE' : row.occupancy || 'OCCUPIED'}`)}
                                    </span>
                                </>
                            );

                            return canPick ? (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => onPick(row)}
                                    className={classes}
                                    aria-label={`${label('wms.field.level')} ${level}, ${label('wms.field.position')} ${positionNumber} — ${row.code}`}
                                >
                                    {body}
                                </button>
                            ) : (
                                <div key={key} className={classes} title={row.code}>
                                    {body}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            <p className="text-center text-xs text-mono-500 dark:text-mono-400">
                {label('wms.collector.tower_legend')}
            </p>
        </div>
    );
};

export default BlockTower;
